import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Typography,
  TablePagination,
  Skeleton,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
} from '@material-ui/core';
import PencilAltIcon from '../../icons/PencilAlt';
import { GetPageFilter } from './ConnectionService';
import SearchIcon from '@material-ui/icons/Search';

const FiltersTable = ({
  filters,
  // onAddFilter,
  onToggleFilter,
  onClickEditFilter,
  isLoading,
  isCheckboxDisabled,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFilters, setFilteredFilters] = useState(filters);
  const searchRef = useRef(null);

  const handleEditFilter = async (filterId) => {
    const res = await GetPageFilter(filterId);
    onClickEditFilter(res);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQueryChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query) {
      const lowercasedQuery = query.toLowerCase();
      const filteredData = filters.filter((filter) =>
        filter.name.toLowerCase().includes(lowercasedQuery) ||
        filter.type.toLowerCase().includes(lowercasedQuery)
      );
      setFilteredFilters(filteredData);
    } else {
      setFilteredFilters(filters);
    }
  };

  useEffect(() => {
    setFilteredFilters(filters);
  }, [filters]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h5">Select Filters</Typography>
          {/* <Grid style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              sx={{
                background:
                  "linear-gradient(to bottom, #072f40, #072f40)",
              }}
              onClick={onAddFilter}
            >
              Add Filter
            </Button>
          </Grid> */}
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ mb: 2, maxWidth: '100%', alignItems: 'center', display: 'flex', width: 300 }}>
            <TextField
              fullWidth
              inputRef={searchRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                style: {
                  height: 43,
                },
              }}
              onChange={handleQueryChange}
              placeholder="Search Filters"
              value={searchQuery}
              variant="outlined"
            />
          </Box>
          <TableContainer component={Paper} style={{ maxHeight: "65vh", minHeight: "30vh", overflow: "auto" }}>
            {isLoading ? (
              <>
                <Skeleton sx={{ ml: 2, mr: 2, height: 70 }} />
                {Array.from(Array(5).keys()).map((index) => (
                  <Skeleton key={index} sx={{ ml: 2, mr: 2, mt: -2, height: 70 }} />
                ))}
              </>
            ) : (
              <>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell> </TableCell> {/* Checkbox column */}
                      <TableCell>Page Filter Name</TableCell>
                      <TableCell>Page Filter Type</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFilters
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((filter) => (
                        <TableRow key={filter.id}>
                          <TableCell>
                            <Checkbox
                              checked={filter.selected}
                              onChange={() => onToggleFilter(filter.id)}
                              disabled={isCheckboxDisabled}
                              sx={{
                                color: filter.selected && isCheckboxDisabled ? 'rgba(0, 0, 0, 0.26)' : '',
                                '&.Mui-checked.Mui-disabled': {
                                  color: 'rgba(0, 0, 0, 0.26)',
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={filter.name} arrow>
                              <div style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '180px'
                              }}>
                                {filter.name}
                              </div>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={filter.type} arrow>
                              <div style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '180px'
                              }}>
                                {filter.type}
                              </div>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              sx={{ p: '5px' }}
                              onClick={() => handleEditFilter(filter.id)}
                            >
                              <PencilAltIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </>
            )}
            {!isLoading && filteredFilters.length === 0 && (
              <Box
                sx={{
                  height: 200,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" sx={{ pl: 0 }}>
                  No data found!
                </Typography>
              </Box>
            )}
          </TableContainer>
          {filteredFilters.length > 0 && (
            <TablePagination
              style={{ marginRight: 40 }}
              component="div"
              count={filteredFilters.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

FiltersTable.propTypes = {
  filters: PropTypes.array.isRequired,
  onAddFilter: PropTypes.func.isRequired,
  onToggleFilter: PropTypes.func.isRequired,
  onClickEditFilter: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isCheckboxDisabled: PropTypes.bool,
};

export default FiltersTable;
